<template>
	<span class="inline-flex">
		<DeclineActionMenu
			:applications="applications"
			:show-selection-context="showSelectionContext"
			:compact="compact"
			:archive-icon="archiveIcon"
			:loading="loading || !!mode"
			:disabled="disabled"
			:button-type="buttonType"
			:button-class="buttonClass"
			:size="size"
			:default-template-name="context ? context.template.name : ''"
			:template-count="context ? context.template_count : 1"
			:recipient-count="context ? context.recipient_ids.length : null"
			:templates-loading="contextLoading"
			:templates-error="contextError"
			@open="loadContext"
			@choose="mode = 'choose'"
			@select="select"
		/>
		<MountingPortal v-if="mode" mount-to="body" append>
			<RejectionTemplateChooser
				ref="chooser"
				:value="true"
				:mode="mode"
				:applications="selectedApplications"
				:initial-context="context"
				@close="mode = null"
				@complete="complete"
			/>
		</MountingPortal>
	</span>
</template>

<script>
import DeclineActionMenu from './DeclineActionMenu.vue'
import RejectionTemplateChooser from './RejectionTemplateChooser.vue'
import { guardModalEscape } from '~/utils/guardModalEscape'

export default {
	name: 'DeclineApplicationAction',
	components: { DeclineActionMenu, RejectionTemplateChooser },
	data: () => ({
		context: null,
		contextLoading: false,
		contextError: false,
		mode: null,
		request: 0,
		releaseEscapeGuard: null
	}),
	computed: {
		selectionKey() {
			return this.applicationId !== null
				? String(this.applicationId)
				: this.applications.map(application => application.id).join(',')
		},
		selectedApplications() {
			if (this.applicationId === null) return this.applications
			return [
				this.$store?.state.application.applications.find(
					application => application.id == this.applicationId
				) ||
					this.applications.find(
						application => application.id == this.applicationId
					) || { id: this.applicationId }
			]
		}
	},
	watch: {
		mode(value) {
			this.releaseEscapeGuard?.()
			this.releaseEscapeGuard = value
				? guardModalEscape(() => this.$refs.chooser?.close())
				: null
		},
		selectionKey() {
			this.context = null
			this.contextLoading = false
			this.request++
			this.mode = null
		}
	},
	beforeDestroy() {
		this.releaseEscapeGuard?.()
	},
	props: {
		disabled: { type: Boolean, default: false },
		applicationId: {
			type: [String, Number],
			default: null
		},
		applications: {
			type: Array,
			default: () => []
		},
		showSelectionContext: {
			type: Boolean,
			default: false
		},
		compact: {
			type: Boolean,
			default: false
		},
		archiveIcon: {
			type: Boolean,
			default: false
		},
		loading: {
			type: Boolean,
			default: false
		},
		buttonType: {
			type: String,
			default: 'danger-light'
		},
		buttonClass: {
			type: String,
			default: ''
		},
		size: {
			type: String,
			default: null,
			validator: value => !value || ['sm', 'lg'].includes(value)
		}
	},
	methods: {
		select(sendRejectionEmail) {
			if (this.disabled || this.loading || this.mode) return
			if (
				sendRejectionEmail &&
				(!this.context || this.contextLoading || this.contextError)
			)
				return
			this.mode = sendRejectionEmail ? 'quick' : 'none'
		},
		async loadContext() {
			const request = ++this.request
			this.contextLoading = true
			this.contextError = false
			try {
				const context = await this.$store.dispatch(
					'rejection/preview',
					{
						application_ids: this.selectedApplications.map(
							application => application.id
						)
					}
				)
				if (request === this.request) this.context = context
			} catch (error) {
				if (request === this.request) this.contextError = true
			} finally {
				if (request === this.request) this.contextLoading = false
			}
		},
		complete(result) {
			const sendRejectionEmail = this.mode !== 'none'
			this.mode = null
			if (!result.declined_count) return
			if (this.applicationId !== null) {
				this.$emit(
					'update',
					this.applicationId,
					'denied',
					sendRejectionEmail,
					result
				)
				return
			}

			this.$emit('update', 'denied', sendRejectionEmail, result)
		}
	}
}
</script>
